import Grid from '@material-ui/core/Grid';
import { createStyles, makeStyles, Theme } from '@material-ui/core';

import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

import AddBox from '@material-ui/icons/AddBox';

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { baseTheme } from '../../../theme';

import { GroupModal, IGroupFormData } from '../../group-management/';
import { loadGroups } from '../../../store';
import { clearGroups, createGroupAction, updateListConfig } from '../../../store/groups/actions';

interface IGroupInputProps {
  data: any;
  handleSaveCallback: Function;
  onOpen: Function;
  postType?: string | null;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    input: {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      '& .MuiFormLabel-root': {
        color: theme.palette.text.primary,
      },
    },
    label: {
      color: theme.palette.text.primary,
    },
    publicColor: {
      color: theme.palette.primary.main,
      paper: {
        backgroundColor: theme.palette.primary.main,
      },
    },
    privateColor: {
      color: theme.palette.secondary.main,
      paper: {
        backgroundColor: theme.palette.primary.main,
      },
    },
    staticColor: {
      color: theme.palette.text.primary,
    },
    option: {
      padding: 0,
      '&:hover': {
        color: theme.palette.background.paper,
      },
    },
    closedGroup: {
      width: '100%',
      height: '100%',
      paddingTop: '6px',
      paddingLeft: '16px',
      paddingRight: '16px',
      paddingBottom: '6px',
      '&[data-focus="true"]': {
        backgroundColor: theme.palette.secondary.main,
      },
      '&:hover': {
        backgroundColor: theme.palette.secondary.main,
      },
    },
    publicGroup: {
      width: '100%',
      height: '100%',
      paddingTop: '6px',
      paddingLeft: '16px',
      paddingRight: '16px',
      paddingBottom: '6px',
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
      },
    },
  }),
);

const defaultFormValues: IGroupFormData = {
  id: '',
  name: '',
  type: 'Private',
};

export default function GroupInputField(props: IGroupInputProps) {
  const { handleSaveCallback, onOpen, data: { group, posted }, postType = null } = props;
  const classes = useStyles(baseTheme);
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const [formValues, setFormValues] = useState<IGroupFormData | null>(defaultFormValues);
  const currUser = useSelector((state: any) => state.auth.currUser);
  const groupsFetched = useSelector((state: any) => state.groups.fetched);
  const groupsList = useSelector((state: any) => state.groups.list);
  const listConfig = useSelector((state: any) => state.groups.listConfig);
  let timer: any;

  useEffect(() => {
    if (currUser && !groupsFetched && listConfig) {
      loadGroups(currUser.uid, listConfig, dispatch);
    }
  }, [currUser, groupsFetched, listConfig, dispatch]);

  const groupSearch = (event: any) => {
    const updatedConfig = {
      ...listConfig,
      search: event.target.value,
    };

    updateListConfig(updatedConfig, dispatch);
    clearTimeout(timer);
    timer = setTimeout(() => {
      clearGroups(dispatch);
    }, 500);
  };

  const handleAddGroup = (event: any) => {
    setIsOpen(true);
  };

  const handleCallBack = (values: IGroupFormData | null) => {
    if (values) {
      createGroupAction(currUser, values, dispatch);
      clearGroups(dispatch);
    }

    setIsOpen(false);
  };

  const handleFormChange = (event: any, value: any) => {
    value ?
      setFormValues({
        id: value.id,
        name: value.name,
        type: value.type,
      })
      : setFormValues(defaultFormValues);
  };

  const handleSave = (event: any) => {
    handleSaveCallback(formValues);

    updateListConfig({
      ...listConfig,
      search: '',
    }, dispatch);

  };

  const handleGroupSelectorOpen = (event: any) => {
    onOpen(event);
  };

  const colorSelector = postType === 'Public'
    ? classes.publicColor
    : postType === 'Private'
      ? classes.privateColor
      : classes.staticColor;

  const renderInput = (params: any) => {
    const paramsModified = {
      ...params,
      InputProps: {
        ...params.InputProps,
      },
    };
    return (<TextField
      {...paramsModified}
      label="CLICK HERE TO SELECT A CLOSED OR PUBLIC GROUP"
      variant="outlined"
      onChange={groupSearch}
      size="small"
      placeholder="To have a group show up here, you must first create it at Groups page."
    />);
  };
  const renderOption = (option: any) => {
    const styleClasses = (option.type === 'Closed')
      ? classes.closedGroup
      : classes.publicGroup;
    return (<div className={styleClasses}>{option.name}</div>);
  };
  return (
    <Grid item xs={12} className={classes.input}>
      <Autocomplete
        id="group-assign"
        options={groupsList}
        classes={classes}
        getOptionLabel={(option: any) => option.name || ''}
        getOptionSelected={
          (option: any) =>
            option.name === formValues?.name
            || option.name === '""'
        }
        renderInput={renderInput}
        renderOption={renderOption}
        value={group}
        onChange={handleFormChange}
        onBlur={handleSave}
        disabled={posted}
        onOpen={handleGroupSelectorOpen}
      />
      <GroupModal
        open={isOpen}
        groupType={formValues?.type}
        handleCallBack={handleCallBack}
      />
    </Grid>
  );
}
